/**
 * Customer specific configuration.
 *
 * Every customer has a directory below `public/themes/<name>/` with a `theme.json` (this structure), the icons,
 * the background images and the legal pages. The deployment selects the theme by setting the `THEME` environment
 * variable of the container; Caddy (production) and the Vite dev server map `/theme/*` to that directory.
 */
export interface ThemeConfig {
  /** Keycloak client id, also the id the backend uses to look up the Keycloak configuration. */
  clientId: string;
  /** Id of the event in the backend. */
  eventId: number;
  /** Browser page title. */
  title: string;
  /** Title in the app bar. */
  appBarTitle: string;
  /** Color of the browser chrome (meta theme-color), usually the primary color or a slightly darker one. */
  themeColor: string;
  primaryColor: string;
  secondaryColor: string;
  /** Text color on primary and secondary colored surfaces. */
  contrastText: string;
  backgroundColor: string;
  drawerColor: string;
  /** Whether card headers are drawn in the secondary color. */
  coloredCardHeader: boolean;
}

/** Base URL of the theme files, see `etc/Caddyfile` and `vite.config.ts`. */
export const THEME_BASE_URL = '/theme';

let config: ThemeConfig | undefined;

export async function loadThemeConfig(): Promise<ThemeConfig> {
  const response = await fetch(themeUrl('theme.json'), {cache: 'no-cache'});
  if (!response.ok) {
    throw new Error(`Could not load the theme configuration (${response.status})`);
  }
  config = (await response.json()) as ThemeConfig;
  return config;
}

/** Makes a configuration available without loading it, e.g. in tests. */
export function setThemeConfig(themeConfig: ThemeConfig | undefined) {
  config = themeConfig;
}

export function themeConfig(): ThemeConfig {
  if (!config) {
    throw new Error('The theme configuration has not been loaded yet');
  }
  return config;
}

/** URL of a file of the current theme, e.g. `themeUrl('bg.png')`. */
export function themeUrl(file: string): string {
  return `${THEME_BASE_URL}/${file}`;
}
